// Browser-only joiner, broker-less. Mirrors OfflineHost.tsx:
//
//   1. User taps "Scan host QR" → camera opens.
//   2. We scan, decode the offer SDP, build an answer.
//   3. Display the answer SDP as a QR for the host to scan.
//   4. When the DataChannel opens, instantiate P2pSession on top of
//      our OfflinePeerHub adapter — same code path the online mode
//      uses for clock-sync + scheduled playback.

import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { OfflinePeerHub } from './OfflinePeerHub'
import { P2pSession } from './p2pSession'
import { acceptOffer, type AnswerHandle } from './offlineSignaling'
import { QrScanner } from './QrScanner'

type Phase =
  | 'idle'              // showing the "Scan host code" button
  | 'scanning-offer'    // camera open, looking for host's QR
  | 'building-answer'   // running setRemoteDescription + createAnswer
  | 'show-answer'       // answer QR displayed, waiting for DC open
  | 'tap-to-play'       // DC open, user must tap to unlock audio
  | 'waiting-for-audio'
  | 'playing'
  | 'error'

export function OfflineJoin({ onExit }: { onExit: () => void }) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [answerQr, setAnswerQr] = useState<string | null>(null)
  const [answerText, setAnswerText] = useState<string | null>(null)
  const [volume, setVolume] = useState(0.8)
  const [latencyOffset, setLatencyOffset] = useState(0)
  const [rtt, setRtt] = useState<number | null>(null)
  const [offset, setOffset] = useState<number | null>(null)
  const [deviceLatency, setDeviceLatency] = useState<number | null>(null)

  const hubRef = useRef<OfflinePeerHub | null>(null)
  const handleRef = useRef<AnswerHandle | null>(null)
  const sessionRef = useRef<P2pSession | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy()
      sessionRef.current = null
      void ctxRef.current?.close()
      ctxRef.current = null
      hubRef.current?.destroy()
      hubRef.current = null
      if (handleRef.current) {
        try { handleRef.current.pc.close() } catch { /* noop */ }
        handleRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    sessionRef.current?.setVolume(volume)
  }, [volume])

  useEffect(() => {
    sessionRef.current?.scheduler.setLatencyOffsetUs(latencyOffset * 1000)
  }, [latencyOffset])

  // Reconnection timeout for offline mode
  useEffect(() => {
    if ((phase as string) !== 'reconnecting') return

    const timeout = setTimeout(() => {
      setError('Connection timed out. Host is unreachable.')
      setPhase('error')
      if (handleRef.current) {
        try { handleRef.current.pc.close() } catch { /* noop */ }
      }
    }, 25_000) // 25 seconds timeout

    return () => clearTimeout(timeout)
  }, [phase])

  const onOfferScanned = async (text: string) => {
    setPhase('building-answer')
    setError(null)
    try {
      const handle = await acceptOffer(text)
      handleRef.current = handle

      // Monitor ICE connection state changes for auto-reconnection
      handle.pc.addEventListener('connectionstatechange', () => {
        const state = handle.pc.connectionState
        // eslint-disable-next-line no-console
        console.info(`[Multispeaker offline join] connectionState changed to: ${state}`)
        if (state === 'disconnected') {
          setPhase('reconnecting' as any)
        } else if (state === 'connected') {
          if (sessionRef.current) {
            setPhase('playing')
          } else {
            setPhase('tap-to-play')
          }
        } else if (state === 'failed' || state === 'closed') {
          setError('WebRTC connection failed.')
          setPhase('error')
        }
      })

      const svg = await QRCode.toString(handle.answer, {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 320,
        color: { dark: '#0b0b0c', light: '#ffffff' },
      })
      setAnswerQr(svg)
      setAnswerText(handle.answer)
      setPhase('show-answer')

      const hub = new OfflinePeerHub()
      hubRef.current = hub

      // The DataChannel doesn't actually exist on our side yet — it
      // arrives via pc.addEventListener('datachannel') once the host
      // scans our answer and the handshake completes. Adopt it into
      // the hub the moment it shows up.
      handle.dcReady.then((dc) => {
        hub.adopt(dc, { pc: handle.pc })
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[Multispeaker join] dcReady rejected', e)
      })

      // When the channel opens we still need a user gesture to spin
      // up the AudioContext — show the "Tap to play" affordance.
      handle.connected.then(() => {
        setPhase('tap-to-play')
      }).catch((e) => {
        setError(e instanceof Error ? e.message : String(e))
        setPhase('error')
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setPhase('error')
    }
  }

  const copyAnswerText = async () => {
    if (!answerText) return
    try { await navigator.clipboard.writeText(answerText) } catch { /* noop */ }
  }

  const tapToPlay = async () => {
    const hub = hubRef.current
    if (!hub) return
    if (sessionRef.current) return
    try {
      if ('audioSession' in navigator) {
        try {
          (navigator as any).audioSession.type = 'playback'
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[Multispeaker offline joiner] Failed to set audioSession.type to playback:', e)
        }
      }
      const ctx = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' })
      await ctx.resume()
      ctxRef.current = ctx
      const session = new P2pSession({
        ctx,
        hub,
        onPlaying: () => setPhase('playing'),
        onDiagnostics: (rttMs, offsetMs, outputLatencyMs) => {
          setRtt(rttMs)
          setOffset(offsetMs)
          setDeviceLatency(outputLatencyMs)
        },
      })
      session.setVolume(volume)
      session.scheduler.setLatencyOffsetUs(latencyOffset * 1000)
      sessionRef.current = session
      setPhase('waiting-for-audio')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setPhase('error')
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-between px-6 py-10 no-select">
      <header className="text-center">
        <h1 className="text-xl font-semibold tracking-tight">Multispeaker</h1>
        <p className="mt-1 text-xs text-zinc-500">Offline pairing</p>
      </header>

      <main className="flex flex-col items-center justify-center text-center">
        {error && (
          <div className="mb-4 rounded-md bg-red-950/50 px-3 py-2 text-sm text-red-300 ring-1 ring-red-900/50">
            {error}
          </div>
        )}

        {phase === 'idle' && (
          <>
            <p className="mb-6 text-sm text-zinc-400">
              On the host device, tap <strong className="text-zinc-200">Start pairing</strong>.
              You'll get a QR code — scan it here.
            </p>
            <button
              type="button"
              onClick={() => setPhase('scanning-offer')}
              className="rounded-full bg-blue-500 px-10 py-4 text-lg font-medium text-white shadow-lg active:scale-95 transition"
            >
              Scan host code
            </button>
          </>
        )}

        {phase === 'building-answer' && (
          <p className="text-sm text-zinc-300">Building answer…</p>
        )}

        {phase === 'show-answer' && answerQr && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-zinc-300">
              Now show this back to the host — tap{' '}
              <strong className="text-zinc-100">Scan answer QR</strong> on their screen.
            </p>
            <div
              className="overflow-hidden rounded-lg ring-1 ring-zinc-800"
              style={{ width: 320, height: 320, background: '#ffffff' }}
              dangerouslySetInnerHTML={{ __html: answerQr }}
            />
            <button
              type="button"
              onClick={copyAnswerText}
              className="rounded-md bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-100 ring-1 ring-zinc-700"
            >
              Copy code
            </button>
            <p className="text-xs text-zinc-500">Waiting for the host to scan…</p>
          </div>
        )}

        {phase === 'tap-to-play' && (
          <button
            type="button"
            onClick={() => void tapToPlay()}
            className="rounded-full bg-blue-500 px-10 py-4 text-lg font-medium text-white shadow-lg active:scale-95 transition"
          >
            Tap to play
          </button>
        )}

        {phase === 'waiting-for-audio' && (
          <p className="text-sm text-zinc-300">Waiting for the host to share audio…</p>
        )}

        {phase === 'playing' && (
          <p className="text-sm text-zinc-300">Listening</p>
        )}

        {phase === 'reconnecting' as any && (
          <p className="text-sm text-amber-400 animate-pulse font-medium">
            Connection lost — trying to reconnect…
          </p>
        )}

        {(phase === 'playing' || phase === 'waiting-for-audio' || phase === 'reconnecting' as any) && (
          <div className="mt-8 w-full max-w-xs space-y-6">
            <div>
              <label className="mb-2 block text-sm text-zinc-400">Volume</label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
              <div className="mt-1 text-right text-xs text-zinc-500 font-mono">
                {Math.round(volume * 100)}%
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/20 p-4 text-left space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-400">Sync Adjust</span>
                <button
                  type="button"
                  onClick={() => setLatencyOffset(0)}
                  disabled={latencyOffset === 0}
                  className="text-[10px] text-zinc-500 hover:text-zinc-300 disabled:opacity-50 underline"
                >
                  reset
                </button>
              </div>
              <div className="space-y-1">
                <input
                  type="range"
                  min={-150}
                  max={150}
                  step={5}
                  value={latencyOffset}
                  onChange={(e) => setLatencyOffset(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-zinc-500 font-mono">
                  <span>-150ms (earlier)</span>
                  <span className={latencyOffset > 0 ? "text-emerald-400" : latencyOffset < 0 ? "text-amber-400" : ""}>
                    {latencyOffset > 0 ? `+${latencyOffset}` : latencyOffset}ms
                  </span>
                  <span>+150ms (later)</span>
                </div>
              </div>

              <details className="text-left border-t border-zinc-900 pt-3">
                <summary className="cursor-pointer text-[10px] text-zinc-400 select-none hover:text-zinc-200">
                  Diagnostics & Latency Info
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] font-mono text-zinc-500">
                  <div>Ping (RTT):</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {rtt !== null ? `${rtt.toFixed(1)} ms` : 'analyzing…'}
                  </div>
                  <div>Clock Offset:</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {offset !== null ? `${offset > 0 ? '+' : ''}${offset.toFixed(1)} ms` : 'analyzing…'}
                  </div>
                  <div>Device Latency:</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {deviceLatency !== null ? (
                      <span>
                        {deviceLatency} ms
                        <span className={`ml-1 text-[9px] font-medium ${deviceLatency > 40 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {deviceLatency > 40 ? 'Wireless/BT' : 'Speaker/Cable'}
                        </span>
                      </span>
                    ) : 'analyzing…'}
                  </div>
                  <div>Sync Status:</div>
                  <div className="text-right">
                    {rtt === null ? (
                      <span className="text-zinc-500">analyzing…</span>
                    ) : rtt < 40 ? (
                      <span className="text-emerald-400">● Good Sync</span>
                    ) : rtt < 100 ? (
                      <span className="text-amber-400">● High Jitter</span>
                    ) : (
                      <span className="text-red-400 font-semibold">● Congested / Hotspot Lag</span>
                    )}
                  </div>
                </div>
                {rtt !== null && rtt >= 50 && (
                  <p className="mt-2 text-[9px] text-zinc-500 leading-normal">
                    ⚠️ Network delay is high. Use the slider above to align with other devices. If host playback is enabled, disabling it on the host can also improve sync.
                  </p>
                )}
              </details>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center">
        <button
          type="button"
          onClick={onExit}
          className="text-xs text-zinc-500 underline-offset-2 hover:underline"
        >
          leave session
        </button>
      </footer>

      {phase === 'scanning-offer' && (
        <QrScanner
          prompt="Scan the host's pairing QR"
          onCancel={() => setPhase('idle')}
          onDecoded={(text) => void onOfferScanned(text)}
        />
      )}
    </div>
  )
}
